export const dictionary = {
  es: {
    nav: {
      marco: 'Marco',
      aplicaciones: 'Aplicaciones',
      motor: 'Motor',
      timeline: 'Timeline',
      papers: 'Papers',
      autor: 'Autor',
      contacto: 'Contacto',
      leerPaper: 'Leer Paper',
    },
    hero: {
      eyebrow: 'Un nuevo marco estructural · 2025',
      title: 'CLVN–LRR™',
      subtitle:
        'Un nuevo marco estructural para modelar latencia, resonancia y reversibilidad en sistemas complejos.',
      description:
        'Desarrollado por Lionel Gustavo Raggio, CLVN–LRR™ redefine la noción de compresión estructural y conservación latente del valor, abriendo nuevas aplicaciones en inteligencia artificial, predicción, mercados, neurociencia y modelado dinámico.',
      btnExplore: 'Explorar el Marco',
      btnPaper: 'Leer Paper',
      btnApps: 'Ver Aplicaciones',
      btnContact: 'Contacto',
    },
    whatIs: {
      title: '¿Qué es CLVN–LRR™?',
      p1: 'El Teorema de la Conservación Latente del Valor Numérico (CLVN) y la Ley de Resonancia y Reversibilidad (LRR) proponen una arquitectura matemática inédita.',
      p2: 'A diferencia de los modelos tradicionales donde la compresión implica pérdida de información, CLVN-LRR™ postula que el valor no desaparece, sino que transita a un estado latente comprimido, capaz de re-manifestarse mediante resonancia estructural.',
      equationCaption:
        'No representa desaparición del valor. Representa transición a un estado latente comprimido.',
      chips: {
        latencia: 'Latencia Matemática',
        reversibilidad: 'Compresión Reversible',
        resonancia: 'Resonancia Estructural',
        sistemas: 'Sistemas Complejos',
      },
    },
    apps: {
      title: 'Aplicaciones',
      viewCase: 'Ver caso →',
      close: 'Cerrar',
      useCases: 'Casos de uso',
      items: [
        {
          id: 'ia',
          title: 'Inteligencia Artificial',
          desc: 'Optimización de redes neuronales mediante estados latentes reversibles.',
          fullDesc:
            'CLVN-LRR™ permite a los modelos de IA comprimir pesos y activaciones en estados de latencia profunda sin pérdida de información, reduciendo drásticamente el costo computacional durante la inferencia.',
          cases: [
            'Compresión de LLMs',
            'Memoria asociativa latente',
            'Reducción de alucinaciones',
          ],
        },
        {
          id: 'mercados',
          title: 'Mercados Financieros',
          desc: 'Modelado de latencia de valor y predicción de resonancias de mercado.',
          fullDesc:
            'El marco proporciona herramientas para identificar valor latente en activos antes de que se manifieste en el precio visible, modelando colapsos y expansiones como ondas de resonancia.',
          cases: [
            'Detección temprana de burbujas',
            'Modelado de liquidez latente',
            'Arbitraje estructural',
          ],
        },
        {
          id: 'predictivos',
          title: 'Sistemas Predictivos',
          desc: 'Anticipación de eventos complejos mediante análisis de ondas resonantes.',
          fullDesc:
            'Aplicando la ecuación central, los sistemas pueden prever la re-manifestación de variables que actualmente se encuentran en estado de compresión extrema (10^-100).',
          cases: [
            'Predicción de fallos en infraestructura',
            'Análisis de tendencias a largo plazo',
          ],
        },
        {
          id: 'neurociencia',
          title: 'Neurociencia',
          desc: 'Mapeo de la conservación de información en redes sinápticas.',
          fullDesc:
            'Proporciona un modelo matemático para entender cómo los recuerdos se comprimen y recuperan, tratando la memoria como un fenómeno de resonancia estructural.',
          cases: [
            'Modelado de memoria a largo plazo',
            'Simulación de plasticidad sináptica',
          ],
        },
        {
          id: 'clima',
          title: 'Clima y Modelado Atmosférico',
          desc: 'Simulación de sistemas caóticos con variables latentes.',
          fullDesc:
            'Mejora la precisión de los modelos climáticos al contabilizar la energía y variables que entran en estados latentes antes de desencadenar eventos meteorológicos extremos.',
          cases: [
            'Predicción de anomalías térmicas',
            'Modelado de corrientes oceánicas profundas',
          ],
        },
        {
          id: 'logistica',
          title: 'Logística Inteligente',
          desc: 'Optimización de cadenas de suministro complejas.',
          fullDesc:
            'Identifica cuellos de botella latentes antes de que afecten el flujo visible, permitiendo una reestructuración dinámica y reversible de las rutas.',
          cases: ['Enrutamiento predictivo', 'Gestión de inventario latente'],
        },
        {
          id: 'compresion',
          title: 'Compresión de Información',
          desc: 'Nuevos paradigmas de almacenamiento sin pérdida.',
          fullDesc:
            'Algoritmos basados en CLVN logran ratios de compresión teóricamente superiores al límite de Shannon al utilizar latencia estructural en lugar de simple redundancia.',
          cases: [
            'Almacenamiento genómico',
            'Archivado de datos a ultra-largo plazo',
          ],
        },
        {
          id: 'simbolica',
          title: 'IA Simbólica',
          desc: 'Integración de razonamiento lógico con representaciones latentes.',
          fullDesc:
            'Cierra la brecha entre el aprendizaje profundo y la IA simbólica, permitiendo que los símbolos lógicos existan en estados de superposición latente.',
          cases: [
            'Razonamiento automatizado avanzado',
            'Sistemas expertos adaptativos',
          ],
        },
        {
          id: 'resonancia',
          title: 'Sistemas de Resonancia',
          desc: 'Ingeniería de materiales y estructuras dinámicas.',
          fullDesc:
            'Aplicación en física de materiales para diseñar estructuras que pueden absorber energía, mantenerla en estado latente y liberarla controladamente.',
          cases: ['Materiales metamórficos', 'Amortiguación sísmica avanzada'],
        },
      ],
    },
    motor: {
      title: 'Motor Visual CLVN',
      subtitle: 'Laboratorio interactivo',
      addNode: 'Añadir Nodo',
      triggerCollapse: 'Forzar Colapso',
      reset: 'Reiniciar',
      resonance: 'Resonancia',
      damping: 'Amortiguación',
      radius: 'Radio de Conexión',
    },
    timeline: {
      title: 'Evolución del Marco',
      items: [
        {
          year: '2022',
          title: 'Descubrimiento del Teorema',
          desc: 'Formulación inicial de la ecuación de conservación latente.',
        },
        {
          year: '2023',
          title: 'Evolución del Marco',
          desc: 'Integración de la Ley de Resonancia y Reversibilidad (LRR).',
        },
        {
          year: '2024',
          title: 'Primer Paper',
          desc: 'Publicación del documento fundacional detallando la arquitectura.',
        },
        {
          year: '2024',
          title: 'Sistemas Desarrollados',
          desc: 'Creación del primer motor de simulación en entornos controlados.',
        },
        {
          year: '2025',
          title: 'Aplicaciones',
          desc: 'Implementación en modelos de IA y análisis de mercados.',
        },
        {
          year: '2025',
          title: 'Validaciones',
          desc: 'Pruebas empíricas de compresión reversible sin pérdida.',
        },
      ],
    },
    papers: {
      title: 'Investigación y Papers',
      search: 'Buscar papers...',
      download: 'Descargar PDF',
      viewOnline: 'Ver online',
      items: [
        {
          title:
            'Conservación Latente del Valor Numérico: Fundamentos y Teorema CLVN-LRR',
          date: 'Oct 2024',
          authors: 'L.G. Raggio',
          tags: ['Fundamentos', 'Matemáticas'],
        },
        {
          title:
            'Compresión Reversible mediante λ-Transformación en Redes Neuronales',
          date: 'Ene 2025',
          authors: 'L.G. Raggio',
          tags: ['IA', 'Compresión'],
        },
        {
          title:
            'Modelado de Resonancias Estructurales en Mercados Financieros',
          date: 'Mar 2025',
          authors: 'L.G. Raggio',
          tags: ['Mercados', 'Sistemas Complejos'],
        },
        {
          title: 'Latencia y Memoria: Una Perspectiva desde CLVN',
          date: 'Abr 2025',
          authors: 'L.G. Raggio',
          tags: ['Neurociencia'],
        },
      ],
    },
    math: {
      title: 'Visualizador Matemático',
      subtitle: 'Simulación de la ecuación central y resonancias fractales.',
      lambda: 'Valor λ',
      frequency: 'Frecuencia',
      amplitude: 'Amplitud',
    },
    author: {
      title: 'Sobre el Autor',
      role: 'Investigador independiente y desarrollador del marco CLVN–LRR™',
      vision: 'Visión',
      visionText:
        'Desarrollar arquitecturas matemáticas que permitan comprender y modelar la complejidad oculta de los sistemas dinámicos, superando las limitaciones de los paradigmas de compresión tradicionales.',
      research: 'Investigación',
      researchText:
        'Enfoque multidisciplinario que abarca matemáticas puras, física teórica, ciencias de la computación y análisis de sistemas complejos.',
      approach: 'Enfoque Multidisciplinario',
      approachText:
        'Integración de conceptos de topología, teoría de la información y dinámica no lineal para crear modelos aplicables en el mundo real.',
    },
    contact: {
      title: 'Contacto',
      name: 'Nombre',
      email: 'Email',
      org: 'Organización',
      reason: 'Motivo de contacto',
      reasons: {
        colab: 'Colaboración',
        press: 'Prensa',
        research: 'Investigación',
        other: 'Otro',
      },
      message: 'Mensaje',
      send: 'Enviar Mensaje',
      success:
        'Mensaje enviado correctamente. Nos pondremos en contacto pronto.',
      links: 'Enlaces Directos',
    },
    footer: {
      signature:
        'Lionel Gustavo Raggio · Creator of CLVN–LRR™ · λ(LGR–327B–Ω917)',
      rights: 'Todos los derechos reservados.',
    },
  },
  en: {
    nav: {
      marco: 'Framework',
      aplicaciones: 'Applications',
      motor: 'Engine',
      timeline: 'Timeline',
      papers: 'Papers',
      autor: 'Author',
      contacto: 'Contact',
      leerPaper: 'Read Paper',
    },
    hero: {
      eyebrow: 'A new structural framework · 2025',
      title: 'CLVN–LRR™',
      subtitle:
        'A new structural framework for modeling latency, resonance, and reversibility in complex systems.',
      description:
        'Developed by Lionel Gustavo Raggio, CLVN–LRR™ redefines the notion of structural compression and latent value conservation, opening new applications in artificial intelligence, prediction, markets, neuroscience, and dynamic modeling.',
      btnExplore: 'Explore Framework',
      btnPaper: 'Read Paper',
      btnApps: 'View Applications',
      btnContact: 'Contact',
    },
    whatIs: {
      title: 'What is CLVN–LRR™?',
      p1: 'The Theorem of Latent Conservation of Numerical Value (CLVN) and the Law of Resonance and Reversibility (LRR) propose an unprecedented mathematical architecture.',
      p2: 'Unlike traditional models where compression implies information loss, CLVN-LRR™ postulates that value does not disappear, but transitions to a compressed latent state, capable of re-manifesting through structural resonance.',
      equationCaption:
        'Does not represent value disappearance. Represents transition to a compressed latent state.',
      chips: {
        latencia: 'Mathematical Latency',
        reversibilidad: 'Reversible Compression',
        resonancia: 'Structural Resonance',
        sistemas: 'Complex Systems',
      },
    },
    apps: {
      title: 'Applications',
      viewCase: 'View case →',
      close: 'Close',
      useCases: 'Use cases',
      items: [
        {
          id: 'ia',
          title: 'Artificial Intelligence',
          desc: 'Neural network optimization through reversible latent states.',
          fullDesc:
            'CLVN-LRR™ allows AI models to compress weights and activations into deep latency states without information loss, drastically reducing computational cost during inference.',
          cases: [
            'LLM Compression',
            'Latent associative memory',
            'Hallucination reduction',
          ],
        },
        {
          id: 'mercados',
          title: 'Financial Markets',
          desc: 'Value latency modeling and market resonance prediction.',
          fullDesc:
            'The framework provides tools to identify latent value in assets before it manifests in visible price, modeling collapses and expansions as resonance waves.',
          cases: [
            'Early bubble detection',
            'Latent liquidity modeling',
            'Structural arbitrage',
          ],
        },
        {
          id: 'predictivos',
          title: 'Predictive Systems',
          desc: 'Anticipation of complex events through resonant wave analysis.',
          fullDesc:
            'Applying the central equation, systems can foresee the re-manifestation of variables currently in extreme compression state (10^-100).',
          cases: [
            'Infrastructure failure prediction',
            'Long-term trend analysis',
          ],
        },
        {
          id: 'neurociencia',
          title: 'Neuroscience',
          desc: 'Mapping information conservation in synaptic networks.',
          fullDesc:
            'Provides a mathematical model to understand how memories are compressed and retrieved, treating memory as a structural resonance phenomenon.',
          cases: [
            'Long-term memory modeling',
            'Synaptic plasticity simulation',
          ],
        },
        {
          id: 'clima',
          title: 'Climate & Atmospheric Modeling',
          desc: 'Simulation of chaotic systems with latent variables.',
          fullDesc:
            'Improves climate model accuracy by accounting for energy and variables entering latent states before triggering extreme weather events.',
          cases: ['Thermal anomaly prediction', 'Deep ocean current modeling'],
        },
        {
          id: 'logistica',
          title: 'Smart Logistics',
          desc: 'Optimization of complex supply chains.',
          fullDesc:
            'Identifies latent bottlenecks before they affect visible flow, allowing dynamic and reversible route restructuring.',
          cases: ['Predictive routing', 'Latent inventory management'],
        },
        {
          id: 'compresion',
          title: 'Information Compression',
          desc: 'New lossless storage paradigms.',
          fullDesc:
            'CLVN-based algorithms achieve compression ratios theoretically superior to the Shannon limit by utilizing structural latency instead of simple redundancy.',
          cases: ['Genomic storage', 'Ultra-long-term data archiving'],
        },
        {
          id: 'simbolica',
          title: 'Symbolic AI',
          desc: 'Integration of logical reasoning with latent representations.',
          fullDesc:
            'Bridges the gap between deep learning and symbolic AI, allowing logical symbols to exist in latent superposition states.',
          cases: ['Advanced automated reasoning', 'Adaptive expert systems'],
        },
        {
          id: 'resonancia',
          title: 'Resonance Systems',
          desc: 'Engineering of dynamic materials and structures.',
          fullDesc:
            'Application in materials physics to design structures that can absorb energy, keep it in a latent state, and release it controllably.',
          cases: ['Metamorphic materials', 'Advanced seismic damping'],
        },
      ],
    },
    motor: {
      title: 'CLVN Visual Engine',
      subtitle: 'Interactive laboratory',
      addNode: 'Add Node',
      triggerCollapse: 'Force Collapse',
      reset: 'Reset',
      resonance: 'Resonance',
      damping: 'Damping',
      radius: 'Connection Radius',
    },
    timeline: {
      title: 'Framework Evolution',
      items: [
        {
          year: '2022',
          title: 'Theorem Discovery',
          desc: 'Initial formulation of the latent conservation equation.',
        },
        {
          year: '2023',
          title: 'Framework Evolution',
          desc: 'Integration of the Law of Resonance and Reversibility (LRR).',
        },
        {
          year: '2024',
          title: 'First Paper',
          desc: 'Publication of the foundational document detailing the architecture.',
        },
        {
          year: '2024',
          title: 'Systems Developed',
          desc: 'Creation of the first simulation engine in controlled environments.',
        },
        {
          year: '2025',
          title: 'Applications',
          desc: 'Implementation in AI models and market analysis.',
        },
        {
          year: '2025',
          title: 'Validations',
          desc: 'Empirical tests of lossless reversible compression.',
        },
      ],
    },
    papers: {
      title: 'Research & Papers',
      search: 'Search papers...',
      download: 'Download PDF',
      viewOnline: 'View online',
      items: [
        {
          title:
            'Latent Conservation of Numerical Value: Fundamentals and CLVN-LRR Theorem',
          date: 'Oct 2024',
          authors: 'L.G. Raggio',
          tags: ['Fundamentals', 'Mathematics'],
        },
        {
          title:
            'Reversible Compression via λ-Transformation in Neural Networks',
          date: 'Jan 2025',
          authors: 'L.G. Raggio',
          tags: ['AI', 'Compression'],
        },
        {
          title: 'Modeling Structural Resonances in Financial Markets',
          date: 'Mar 2025',
          authors: 'L.G. Raggio',
          tags: ['Markets', 'Complex Systems'],
        },
        {
          title: 'Latency and Memory: A CLVN Perspective',
          date: 'Apr 2025',
          authors: 'L.G. Raggio',
          tags: ['Neuroscience'],
        },
      ],
    },
    math: {
      title: 'Mathematical Visualizer',
      subtitle: 'Simulation of the central equation and fractal resonances.',
      lambda: 'λ Value',
      frequency: 'Frequency',
      amplitude: 'Amplitude',
    },
    author: {
      title: 'About the Author',
      role: 'Independent researcher and developer of the CLVN–LRR™ framework',
      vision: 'Vision',
      visionText:
        'To develop mathematical architectures that allow understanding and modeling the hidden complexity of dynamic systems, overcoming the limitations of traditional compression paradigms.',
      research: 'Research',
      researchText:
        'Multidisciplinary approach encompassing pure mathematics, theoretical physics, computer science, and complex systems analysis.',
      approach: 'Multidisciplinary Approach',
      approachText:
        'Integration of concepts from topology, information theory, and nonlinear dynamics to create models applicable in the real world.',
    },
    contact: {
      title: 'Contact',
      name: 'Name',
      email: 'Email',
      org: 'Organization',
      reason: 'Reason for contact',
      reasons: {
        colab: 'Collaboration',
        press: 'Press',
        research: 'Research',
        other: 'Other',
      },
      message: 'Message',
      send: 'Send Message',
      success: 'Message sent successfully. We will be in touch soon.',
      links: 'Direct Links',
    },
    footer: {
      signature:
        'Lionel Gustavo Raggio · Creator of CLVN–LRR™ · λ(LGR–327B–Ω917)',
      rights: 'All rights reserved.',
    },
  },
}
